import React, { useState, useEffect } from 'react';
import { ThemeProvider } from './ThemeProvider';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login        from './components/Login.jsx';
import Register     from './components/Register.jsx';
import PredictionPage from './components/PredictionPage.jsx';
import SettingsPage from './components/SettingsPage.jsx';
import PersonasPage from './components/PersonasPage.jsx';
import AddEventPage from './components/AddEventPage.jsx';
import DisclaimerPage from './components/DisclaimerPage.jsx';
import Navigation   from './components/Navigation.jsx';
import UpgradeModal from './components/UpgradeModal.jsx';
import PaymentPage  from './pages/PaymentPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import TestPayment from './pages/TestPayment';
import Disco from './pages/Disc';
import UpgradePage from './pages/UpgradePage';
import LandingPage from './pages/LandingPage';
import HowItWorksPage from './pages/HowItWorksPage';
import PhilosophyPage from './pages/PhilosophyPage';
import MethodPage from './pages/MethodPage';
import CaregiversPage from './pages/CaregiversPage';
import FAQPage from './pages/FAQPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import PricingPage from './pages/PricingPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost'; 
import './App.css';
import { applyTheme, getTheme } from './themes';

import AdvertiseWithUs from './pages/AdvertiseWithUs';
import AdminAds from './pages/AdminAds';

// In routes:


// In routes:


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    const theme = process.env.REACT_APP_THEME || 'green';
    console.log('Applying theme:', theme);
    applyTheme(getTheme(theme));
    window.currentTheme = theme;

    if (token) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const handleUpgrade = (tier) => {
    fetch(`${API_URL}/stripe/create-checkout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ tier })
    })
    .then(res => res.json())
    .then(data => {
      window.location.href = "/payment";
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="app">
        {user && <Navigation user={user} logout={logout} />}
        {user && <EmailVerificationBanner user={user} token={token} />}
        
        <Routes>
          {/* Public Routes - No Auth Required */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/philosophy" element={<PhilosophyPage />} />
          <Route path="/method" element={<MethodPage />} />
          <Route path="/caregivers" element={<CaregiversPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/advertise" element={<AdvertiseWithUs />} />
          
          {/* Email Verification Routes */}
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          
          {/* Auth Routes */}
          <Route 
            path="/login" 
            element={!user ? <Login onLogin={login} /> : <Navigate to="/app" />} 
          />
          <Route 
            path="/register" 
            element={!user ? <Register onRegister={login} /> : <Navigate to="/app" />} 
          />
          
          {/* Protected App Routes */}
          <Route 
            path="/app" 
            element={user ? (
              <PredictionPage 
                user={user} 
                token={token}
                onUpgradeClick={() => setShowUpgrade(true)}
              />
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/settings" 
            element={user ? (
              <SettingsPage 
                user={user} 
                token={token}
                onUpdate={fetchUserProfile}
                onUpgradeClick={() => setShowUpgrade(true)}
              />
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/personas" 
            element={user ? (
              <PersonasPage 
                user={user} 
                token={token}
                onUpdate={fetchUserProfile}
                onUpgradeClick={() => setShowUpgrade(true)}
              />
            ) : <Navigate to="/login" />} 
          />
          <Route
            path="/add-event"
            element={user ? (
              <AddEventPage
                user={user}
                token={token}
                onUpdate={fetchUserProfile}
                onUpgradeClick={() => setShowUpgrade(true)}
              />
            ) : <Navigate to="/login" />}
          />

          <Route
            path="/admin/ads"
            element={
              <AdminAds
                token={token}
                user={user}
              />
            }
          />
 
          <Route 
            path="/payment" 
            element={<PaymentPage />} 
          />

          <Route 
            path="/payment-success" 
            element={<PaymentSuccessPage />} 
          />

          <Route
            path="/disco"
            element={<Disco />}
          />

          <Route 
            path="/upgrade"
            element={user ? (
              <UpgradePage user={user} token={token} />
            ) : <Navigate to="/login" />}  
          />

          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} /> 

          <Route path="/test-payment" element={<TestPayment />} />
        </Routes>

        {showUpgrade && (
          <UpgradeModal
            currentTier={user?.tier}
            onClose={() => setShowUpgrade(false)}
            onUpgrade={handleUpgrade}
          />
        )}
      </div>
    </BrowserRouter>
  );
}

export default App;
